/**
 * StampDutySection Component
 *
 * Multiple stamp-duty rows per contract (T113), with a per-contract editable
 * percentage on capture (T114). Presented with the same "Add" button +
 * capture-modal UX as the broker / collateral sections:
 *
 *  - Header shows "Stamp Duty (N)" and an always-visible "Add Stamp Duty"
 *    button (a contract may carry several stamp duties).
 *  - Each row references a `Stamp Duty`-type row from Reference Data →
 *    Placement Fees. Selecting a reference row pre-fills its percentage, which
 *    the user can then override (like the free-text Broker Fees field). The
 *    per-contract percentage is saved on the contract's stamp-duty row; the
 *    reference row's own default is untouched.
 *  - Stamp duty is an upfront, one-off fee = capital_amount × percentage. The
 *    table shows the computed amount alongside the percentage.
 *
 * Rows live in contractStampDuties (form context), persisted by the wizard via
 * the /contracts/:id/stamp-duties CRUD endpoints on save. Each row: { id?,
 * stamp_duty_fees_id, reference, percentage }.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { formatNumber, formatRatePct } from '../../../../utils/formatters';

// Shared anchor-column widths so the Broker / Stamp Duty / Collateral tables
// line up across their cards (first column + Actions column align; middle
// columns flex per table). Keep in sync across the three sections.
// Fixed widths so the duty% + Amount columns sit tight together and Actions
// anchors at the same right-edge position as the Broker / Collateral tables.
const PCT_COL_W = 120;
const AMOUNT_COL_W = 150;
const ACTIONS_COL_W = 96;

function StampDutySection() {
  const { watch } = useFormContext();
  const {
    refData,
    isEditable,
    contractStampDuties,
    setContractStampDuties,
  } = useContractFormContext();

  const disabled = isEditable === false;
  const rows = contractStampDuties || [];

  // Contract capital + currency for the computed (capital × %) amount.
  const capitalAmount = parseFloat(watch('capital_amount')) || 0;
  const investmentCurrencyId = watch('investment_currency_id');
  const currencyCode = (refData.currencies || []).find(c => c.id === investmentCurrencyId)?.currency_code || '';

  // Available stamp-duty reference rows (type = 'Stamp Duty', active).
  const stampDutyOptions = (refData.placementFees || []).filter(
    (f) => f.type === 'Stamp Duty' && f.active !== false
  );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // null = add mode
  const [draft, setDraft] = useState({ stamp_duty_fees_id: '', percentage: '' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  // Ref ids already on the contract (to prevent duplicates in the dropdown),
  // excluding the row currently being edited.
  const usedRefIds = new Set(
    rows
      .map((r, i) => (i === editIndex ? null : r.stamp_duty_fees_id))
      .filter((v) => v != null)
  );
  const selectableOptions = stampDutyOptions.filter(
    (o) => !usedRefIds.has(o.id) || o.id === draft.stamp_duty_fees_id
  );

  const computeAmount = (pct) => {
    const p = parseFloat(pct);
    if (!isFinite(p)) return null;
    return capitalAmount * p / 100;
  };

  const handleOpenAdd = () => {
    setEditIndex(null);
    setDraft({ stamp_duty_fees_id: '', percentage: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (index) => {
    setEditIndex(index);
    setDraft({
      stamp_duty_fees_id: rows[index]?.stamp_duty_fees_id || '',
      // Normalize to a trimmed value so the field shows 0.01 not 0.0100.
      percentage: formatRatePct(rows[index]?.percentage),
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditIndex(null);
    setDraft({ stamp_duty_fees_id: '', percentage: '' });
  };

  // Selecting a reference row (re)pre-fills its percentage. Choosing a
  // different row is a deliberate new selection, so it always refreshes the %;
  // the user can then override by typing in the % field afterwards.
  const handleSelectRef = (refId) => {
    const selected = stampDutyOptions.find((o) => o.id === refId);
    setDraft((prev) => ({
      ...prev,
      stamp_duty_fees_id: refId,
      percentage: selected ? parseFloat(selected.percentage) : prev.percentage,
    }));
  };

  const isSubmitDisabled = () => {
    if (!draft.stamp_duty_fees_id) return true;
    const p = parseFloat(draft.percentage);
    if (!isFinite(p) || p < 0) return true;
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;
    const selected = stampDutyOptions.find((o) => o.id === draft.stamp_duty_fees_id);
    if (!selected) return;
    const newRow = {
      stamp_duty_fees_id: selected.id,
      reference: selected.reference,
      percentage: parseFloat(draft.percentage),
    };
    setContractStampDuties((prev) => {
      const next = [...prev];
      if (editIndex == null) {
        next.push(newRow);
      } else {
        next[editIndex] = { ...next[editIndex], ...newRow };
      }
      return next;
    });
    handleCloseDialog();
  };

  const handleRemove = (index) => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        setContractStampDuties((prev) => prev.filter((_, i) => i !== index));
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  const draftRefRow = stampDutyOptions.find((o) => o.id === draft.stamp_duty_fees_id);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          <Typography variant="h6">
            Stamp Duty ({rows.length})
          </Typography>
        </Box>
        {!disabled && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
          >
            Add Stamp Duty
          </Button>
        )}
      </Box>

      {rows.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography color="textSecondary">
            No stamp duty on this contract. Click "Add Stamp Duty" to record one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            {/* 8-column grid mirroring the Collateral table so Stamp Duty %,
                Amount and Actions land in the same positions across the cards.
                Columns 2–4 and 7 are blank fillers. */}
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Reference</TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: PCT_COL_W }}>Stamp Duty %</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMOUNT_COL_W }}>Amount</TableCell>
                {!disabled && <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const refRow = stampDutyOptions.find((o) => o.id === row.stamp_duty_fees_id);
                const label = row.reference || refRow?.reference || `Ref #${row.stamp_duty_fees_id}`;
                const pct = row.percentage != null ? row.percentage : refRow?.percentage;
                const amount = computeAmount(pct);
                return (
                  <TableRow key={row.id ?? `new-${index}`}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ReceiptLongIcon fontSize="small" color="action" />
                        <Typography variant="body2" fontWeight="medium">{label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="right" sx={{ width: PCT_COL_W }}>
                      <Typography variant="body2">
                        {pct !== '' && pct != null ? `${formatRatePct(pct)}%` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ width: AMOUNT_COL_W }}>
                      <Typography variant="body2">
                        {amount != null ? `${currencyCode} ${formatNumber(amount)}` : '—'}
                      </Typography>
                    </TableCell>
                    {!disabled && (
                      <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>
                        <Tooltip title="Edit stamp duty">
                          <IconButton size="small" onClick={() => handleOpenEdit(index)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove stamp duty">
                          <IconButton size="small" color="error" onClick={() => handleRemove(index)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex == null ? 'Add Stamp Duty' : 'Edit Stamp Duty'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="stamp-duty-ref-label">Stamp Duty *</InputLabel>
                <Select
                  labelId="stamp-duty-ref-label"
                  label="Stamp Duty *"
                  value={draft.stamp_duty_fees_id}
                  onChange={(e) => handleSelectRef(e.target.value)}
                >
                  {selectableOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      No stamp-duty rates available in Reference Data → Placement Fees
                    </MenuItem>
                  )}
                  {selectableOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.reference} — {formatRatePct(o.percentage)}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Stamp Duty (%) *"
                type="number"
                fullWidth
                value={draft.percentage}
                onChange={(e) => setDraft((prev) => ({ ...prev, percentage: e.target.value }))}
                inputProps={{ step: 0.0001, min: 0 }}
                helperText={
                  draftRefRow
                    ? `Pre-filled from reference data (${formatRatePct(draftRefRow.percentage)}%) — editable`
                    : 'Pre-filled from reference data when a rate is selected — editable'
                }
              />
            </Grid>

            {draft.stamp_duty_fees_id && computeAmount(draft.percentage) != null && (
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Amount (capital × %): <strong>{currencyCode} {formatNumber(computeAmount(draft.percentage))}</strong>
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {editIndex == null ? 'Add' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Stamp Duty"
        message="Are you sure you want to remove this stamp duty?"
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default StampDutySection;
